// Chat con Telegram (Bloque 1) -- utilidades del front. El backend es la autoridad de permisos; aquí solo se arma la pantalla.
// Contrato: backend/routes/CONTRATO_TELEGRAM_CHAT.md (todas las rutas cuelgan de /api/telegram).
import api from "./api";

export const ROLES_TG = ["admin", "gerente", "asistente", "bilingue"];
export const puedeTelegram = (user) => !!user && ROLES_TG.includes(user.role);
export const myIdTg = (user) => String(user?._id || user?.id || "");

export const tg = {
  get: (path, config) => api.get(`/telegram${path}`, config),
  post: (path, body, config) => api.post(`/telegram${path}`, body ?? {}, config),
  put: (path, body, config) => api.put(`/telegram${path}`, body ?? {}, config),
  delete: (path, config) => api.delete(`/telegram${path}`, config),
};

export const codigoTg = (e) => {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" ? d.code || "" : "";
};
/** Telegram apagado en el servidor (TELEGRAM_ENABLED distinto de true): 503 telegram_disabled. */
export const esTelegramApagado = (e) => e?.response?.status === 503 && codigoTg(e) === "telegram_disabled";

const MENSAJES = {
  telegram_disabled: "Telegram está apagado en este servidor.",
  telegram_key_missing: "Falta la clave de cifrado del servidor. Avise al administrador técnico; no se guardó nada.",
  not_account_owner: "Solo el dueño de la cuenta puede hacer esto.",
  already_connected: "Ya hay una sesión conectada. Ciérrela primero.",
  no_credentials: "Primero guarde su api_id y su api_hash.",
  no_pending_login: "El código QR ya no está activo. Genere uno nuevo.",
  bad_password: "La contraseña de dos pasos no es correcta.",
  not_connected: "La cuenta de Telegram no está conectada.",
  send_disabled: "El envío está apagado: por ahora Telegram funciona solo en modo lectura.",
  no_write_permission: "No tienes permiso para escribir en este chat.",
  send_in_progress: "Ese mensaje quedó en un estado incierto. Verifica en Telegram antes de reenviarlo.",
  flood_wait: "Telegram pide esperar un momento. Reintenta en unos segundos.",
  session_invalid: "La sesión de Telegram se cayó. El dueño debe volver a conectarla.",
  telegram_error: "No se sabe si el mensaje salió. Verifica en Telegram antes de reenviarlo.",
  send_rate_limit: "Demasiados envíos seguidos. Espera un minuto.",
  owner_required: "Elige de cuál cuenta de Telegram quieres ver los chats.",
  write_requires_view: "Para escribir, la persona también debe poder ver.",
  solo_yo_locked: "Demasiados intentos con la clave. Espera unos minutos.",
  already_solo_yo: "Ese chat ya es «Solo yo».",
  solo_yo_key_required: "Este chat es «Solo yo»: escribe su clave.",
  solo_yo_bad_key: "La clave no es correcta.",
};
export function errorTg(e) {
  const code = codigoTg(e);
  if (code && MENSAJES[code]) return MENSAJES[code];
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.message) return String(d.message);
  const st = e?.response?.status;
  if (st === 404) return "No encontrado o sin acceso.";
  if (st === 403) return "No tienes permiso para esto.";
  if (st === 401) return "Tu sesión venció. Entra de nuevo.";
  if (st === 422) return "Revisa los datos escritos.";
  if (!e?.response) return "Sin conexión con el servidor. Reintenta.";
  return "No se pudo completar. Reintenta.";
}
/** Errores donde reintentar con el MISMO nonce es seguro (el mensaje no salió). */
export const reintentable = (e) => !e?.response || ["flood_wait", "session_invalid"].includes(codigoTg(e));

// ---- Claves de «Solo yo»: SOLO en memoria del navegador (nunca localStorage, nunca logs) ----
const claves = new Map();
const kc = (owner, chat) => `${owner || ""}::${chat}`;
export const guardarClave = (owner, chat, clave) => { claves.set(kc(owner, chat), clave); };
export const leerClave = (owner, chat) => claves.get(kc(owner, chat)) || "";
export const olvidarClave = (owner, chat) => { claves.delete(kc(owner, chat)); };
export const olvidarTodasLasClaves = () => { claves.clear(); };
export const cabeceraClave = (owner, chat) => {
  const k = leerClave(owner, chat);
  return k ? { "X-Telegram-Chat-Key": k } : undefined;
};

/** Identificador único por intento de envío (el servidor lo exige: reintentar con el mismo no duplica). */
export function nuevoNonce() {
  try {
    const a = new Uint8Array(12);
    (globalThis.crypto || window.crypto).getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    return `n${Date.now().toString(16)}${Math.random().toString(16).slice(2, 12)}`.padEnd(12, "0");
  }
}

// ---- Presentación ----
const COLORES_AV = ["#4a8bd0", "#3DA67A", "#E08A2E", "#8E6FD8", "#D86F8E", "#6CC8F0", "#c9a0ff", "#FFA300"];
export function colorAvatar(seed) {
  let h = 0;
  for (const ch of String(seed || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return COLORES_AV[h % COLORES_AV.length];
}
export function iniciales(texto) {
  const p = String(texto || "").trim().split(/\s+/).filter(Boolean);
  if (!p.length) return "?";
  return (p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[1][0]).toUpperCase();
}
export function horaCorta(ts) {
  const d = ts ? new Date(ts) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  return mismoDia ? d.toLocaleTimeString("es", { hour: "numeric", minute: "2-digit" }) : d.toLocaleDateString("es", { day: "numeric", month: "short" });
}
export function urlThumb(owner, chat) {
  const base = api?.defaults?.baseURL || "/api";
  return `${base}/telegram/chats/${encodeURIComponent(chat)}/thumb${owner ? `?owner_id=${encodeURIComponent(owner)}` : ""}`;
}
export function duracion(seg) {
  const s = Math.max(0, Math.round(Number(seg) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
export function tamano(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  return b < 1048576 ? `${Math.round(b / 1024)} KB` : `${(b / 1048576).toFixed(1)} MB`;
}
/** Texto del último mensaje en la lista (vacío = adjunto). */
export const resumenUltimo = (last) => {
  if (!last) return "";
  const t = String(last.text || "");
  const m = /^\[(voice|photo|sticker|file)\]$/.exec(t);
  if (m) return { voice: "🎤 Nota de voz", photo: "📷 Foto", sticker: "Sticker", file: "📎 Archivo" }[m[1]];
  return t;
};

export const ESTADOS_SIN_CHATS = ["sin_conectar", "sin_acceso"];
export const LIMITE_CHATS = 100;

// ---- Tema claro/oscuro (pedido del Founder 2-nov: igual a su Telegram real, que es CLARO): se recuerda por navegador,
// claro por defecto. Cada ventana (módulo, burbuja/ancla, pantalla aparte) lo lee una vez al montar. ----
const TEMA_KEY = "tg.tema.v1";
export function leerTemaTg() {
  try { return localStorage.getItem(TEMA_KEY) === "oscuro" ? "oscuro" : "claro"; } catch (e) { return "claro"; }
}
export function guardarTemaTg(t) {
  try { localStorage.setItem(TEMA_KEY, t === "oscuro" ? "oscuro" : "claro"); } catch (e) { /* sin almacenamiento */ }
}

/** Etiqueta del separador de fecha (centrado, como Telegram): «Hoy», «Ayer» o «1 de noviembre». */
export function etiquetaFecha(ts) {
  const d = ts ? new Date(ts) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const hoy = new Date();
  const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1);
  if (d.toDateString() === hoy.toDateString()) return "Hoy";
  if (d.toDateString() === ayer.toDateString()) return "Ayer";
  return d.toLocaleDateString("es", { day: "numeric", month: "long", ...(d.getFullYear() !== hoy.getFullYear() ? { year: "numeric" } : {}) });
}
/** Clave de día (para agrupar mensajes y decidir cuándo meter un separador de fecha). */
export const diaDe = (ts) => { const d = ts ? new Date(ts) : null; return d && !Number.isNaN(d.getTime()) ? d.toDateString() : ""; };

const RE_URL = /(https?:\/\/[^\s<>]+[^\s<>.,;:!?)\]])/g;
/** Parte el texto de un mensaje en trozos de texto plano y enlaces (como hace Telegram): sin inventar más que detectar http(s)://. */
export function partesConEnlaces(texto) {
  const t = String(texto || "");
  if (!t) return [];
  const partes = [];
  let ultimo = 0;
  for (const m of t.matchAll(RE_URL)) {
    if (m.index > ultimo) partes.push({ tipo: "texto", valor: t.slice(ultimo, m.index) });
    partes.push({ tipo: "enlace", valor: m[0] });
    ultimo = m.index + m[0].length;
  }
  if (ultimo < t.length) partes.push({ tipo: "texto", valor: t.slice(ultimo) });
  return partes;
}
